import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { arrayMove } from '@dnd-kit/sortable';
import { categoriesService, type CategoryReportGroup, type CategoryView } from '../../../../../../services/categories/categories.service';
import type { ProductView } from '../../../../../../services/products/products.service';
import { toHebrewApiErrorMessage } from '../../../../../../services/index/index.service';
import { addFormRow, createRowKey, removeFormRow, updateFormRowField } from '../../../../../../utils/form-row-list';
import {
  getSelectableParentOptions,
  productIdsInCategory,
  sortCategoriesByDisplayOrder,
  sortCategoriesForManager,
} from '../shared/category-manager.utils';

const notifyError = (error: unknown) => {
  if (error instanceof Error) {
    notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
  }
};

export type CategoryFormRow = {
  name: string;
  reportGroup: CategoryReportGroup | null;
  parentCategoryPk: number | null;
  separateKitchenBon: boolean;
  quickNotes: string[];
  rowKey: string;
};

// Factory (not a shared constant): each call needs its own unique rowKey from
// utils/form-row-list.ts's createRowKey, so React keys stay stable per-row.
export const createCategoryFormRow = (): CategoryFormRow => ({
  name: '',
  reportGroup: null,
  parentCategoryPk: null,
  separateKitchenBon: false,
  quickNotes: [],
  rowKey: createRowKey('category-row'),
});

type CategoryEditBaseline = {
  name: string;
  reportGroup: CategoryReportGroup | null;
  parentCategoryPk: number | null;
  separateKitchenBon: boolean;
  quickNotes: string[];
};

export type CategoryManagerControllerArgs = {
  tenantId: string;
  employeeSession: string | null;
  categories: CategoryView[];
  products: ProductView[];
};

// Summary: Category-manager state and guarded async handlers for modal/drawer presentation.
// Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
export function useCategoryManagerController({ tenantId, employeeSession, categories, products }: CategoryManagerControllerArgs) {
  const [rows, setRows] = useState<CategoryFormRow[]>(() => [createCategoryFormRow()]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingBaseline, setEditingBaseline] = useState<CategoryEditBaseline | null>(null);
  const [membershipCategoryId, setMembershipCategoryId] = useState<number | null>(null);
  const [checkedProductIds, setCheckedProductIds] = useState<Set<number>>(new Set());
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [isSavingMembership, setIsSavingMembership] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [reportGroupSavingId, setReportGroupSavingId] = useState<number | null>(null);
  const [parentSavingId, setParentSavingId] = useState<number | null>(null);

  const formInFlightRef = useRef(false);
  const membershipInFlightRef = useRef(false);
  const togglingIdRef = useRef<number | null>(null);
  const reportGroupInFlightRef = useRef<number | null>(null);
  const parentInFlightRef = useRef<number | null>(null);
  const categoryWriteInFlightRef = useRef(false);
  // Baseline membership at open, so save sends only the add/remove delta (never the full set).
  const initialCheckedRef = useRef<Set<number>>(new Set());

  const sorted = useMemo(() => sortCategoriesForManager(categories), [categories]);
  const displayOrder = useMemo(() => sortCategoriesByDisplayOrder(categories), [categories]);
  // Optimistic state holds ONLY the id order; the rendered list is derived from current props.
  const [orderedIds, setOrderedIds] = useState<number[]>(() => displayOrder.map((category) => category.id));
  const reorderInFlightRef = useRef(false);

  // Re-seed the id order whenever the persisted order changes (SSE echo, create, toggle).
  const orderSignature = displayOrder.map((category) => `${category.id}:${category.sortOrder}`).join(',');
  useEffect(() => {
    if (!reorderInFlightRef.current) {
      setOrderedIds(displayOrder.map((category) => category.id));
    }
    // orderSignature is the stable scalar key for the persisted order (Rule 21).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature]);

  // Rows derived from current props via the id order, so content edits show (mistake #86).
  const orderedCategories = useMemo(() => {
    const byId = new Map(displayOrder.map((category) => [category.id, category]));
    const seen = new Set<number>();
    const result: CategoryView[] = [];
    for (const id of orderedIds) {
      const category = byId.get(id);
      if (category) { result.push(category); seen.add(id); }
    }
    for (const category of displayOrder) {
      if (!seen.has(category.id)) result.push(category);
    }
    return result;
  }, [displayOrder, orderedIds]);

  const reorderCategory = useCallback(async (activeId: number, overId: number) => {
    if (!employeeSession || reorderInFlightRef.current || activeId === overId) return;
    const from = orderedIds.indexOf(activeId);
    const to = orderedIds.indexOf(overId);
    if (from < 0 || to < 0) return;
    const previous = orderedIds;
    const next = arrayMove(orderedIds, from, to);
    setOrderedIds(next);
    reorderInFlightRef.current = true;
    try {
      await categoriesService.reorder(next, employeeSession);
    } catch (error) {
      setOrderedIds(previous);
      notifyError(error);
    } finally {
      reorderInFlightRef.current = false;
    }
  }, [employeeSession, orderedIds]);

  const activeProducts = useMemo(() => products.filter((product) => product.isActive), [products]);
  const categoryById = useMemo(() => new Map(categories.map((category) => [category.id, category])), [categories]);
  // Count only active products so the per-category indicator matches what the membership drawer shows.
  const productCountByCategory = useMemo(() => {
    const counts = new Map<number, number>();
    for (const product of activeProducts) {
      if (product.categoryId != null) {
        counts.set(product.categoryId, (counts.get(product.categoryId) ?? 0) + 1);
      }
    }
    return counts;
  }, [activeProducts]);
  const membershipCategory = membershipCategoryId != null ? categoryById.get(membershipCategoryId) ?? null : null;

  // Summary: Seed membership checkboxes from the selected category's current products.
  // Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
  useEffect(() => {
    if (membershipCategoryId === null) {
      setCheckedProductIds(new Set());
      initialCheckedRef.current = new Set();
      return;
    }
    const seed = new Set(productIdsInCategory(products, membershipCategoryId));
    setCheckedProductIds(seed);
    initialCheckedRef.current = new Set(seed);
  }, [membershipCategoryId, products]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingBaseline(null);
    setRows([createCategoryFormRow()]);
  }, []);

  const startEdit = useCallback((category: CategoryView) => {
    setEditingId(category.id);
    setEditingBaseline({ name: category.name, reportGroup: category.reportGroup, parentCategoryPk: category.parentCategoryPk, separateKitchenBon: category.separateKitchenBon, quickNotes: category.quickNotes ?? [] });
    setRows([{ name: category.name, reportGroup: category.reportGroup, parentCategoryPk: category.parentCategoryPk, separateKitchenBon: category.separateKitchenBon, quickNotes: category.quickNotes ?? [], rowKey: `edit-${category.id}` }]);
  }, []);

  const addRow = useCallback(() => {
    setRows((previous) => addFormRow(previous, createCategoryFormRow()));
  }, []);

  const removeRow = useCallback((index: number) => {
    setRows((previous) => removeFormRow(previous, index));
  }, []);

  const updateRowField = useCallback(
    (index: number, field: 'name' | 'reportGroup' | 'parentCategoryPk' | 'separateKitchenBon' | 'quickNotes', value: string | CategoryReportGroup | number | boolean | string[] | null) => {
      setRows((previous) => updateFormRowField(previous, index, field, value));
    },
    [],
  );

  const submitForm = useCallback(async () => {
    if (!employeeSession || formInFlightRef.current || categoryWriteInFlightRef.current) return;

    if (editingId !== null) {
      const row = rows[0];
      const trimmed = row.name.trim();
      if (!trimmed) return;
      // Rule 19: PATCH is diff-only — only fields that actually changed are sent.
      // An edit where neither name nor reportGroup nor parentCategoryPk changed closes without a request.
      if (
        editingBaseline !== null
        && trimmed === editingBaseline.name
        && row.reportGroup === editingBaseline.reportGroup
        && row.parentCategoryPk === editingBaseline.parentCategoryPk
        && row.separateKitchenBon === editingBaseline.separateKitchenBon
        && JSON.stringify(row.quickNotes) === JSON.stringify(editingBaseline.quickNotes)
      ) {
        cancelEdit();
        return;
      }
      const updatePayload: { name?: string; reportGroup?: CategoryReportGroup | null; parentCategoryPk?: number | null; separateKitchenBon?: boolean; quickNotes?: string[] } = {};
      if (editingBaseline === null || trimmed !== editingBaseline.name) {
        updatePayload.name = trimmed;
      }
      if (editingBaseline === null || row.reportGroup !== editingBaseline.reportGroup) {
        updatePayload.reportGroup = row.reportGroup;
      }
      if (editingBaseline === null || row.parentCategoryPk !== editingBaseline.parentCategoryPk) {
        updatePayload.parentCategoryPk = row.parentCategoryPk;
      }
      if (editingBaseline === null || row.separateKitchenBon !== editingBaseline.separateKitchenBon) {
        updatePayload.separateKitchenBon = row.separateKitchenBon;
      }
      if (editingBaseline === null || JSON.stringify(row.quickNotes) !== JSON.stringify(editingBaseline.quickNotes)) {
        updatePayload.quickNotes = row.quickNotes;
      }
      formInFlightRef.current = true;
      categoryWriteInFlightRef.current = true;
      setIsSavingForm(true);
      try {
        await categoriesService.update(editingId, updatePayload, employeeSession);
        cancelEdit();
      } catch (error) {
        notifyError(error);
      } finally {
        formInFlightRef.current = false;
        categoryWriteInFlightRef.current = false;
        setIsSavingForm(false);
      }
      return;
    }

    // Create mode: all-or-nothing client-side validation across every row first.
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      if (!rows[rowIndex].name.trim()) {
        notifyError(new Error(`קטגוריה ${rowIndex + 1}: שם הקטגוריה נדרש`));
        return;
      }
    }

    formInFlightRef.current = true;
    categoryWriteInFlightRef.current = true;
    setIsSavingForm(true);
    try {
      for (const row of rows) {
        await categoriesService.create(tenantId, { name: row.name.trim(), reportGroup: row.reportGroup, parentCategoryPk: row.parentCategoryPk, separateKitchenBon: row.separateKitchenBon, quickNotes: row.quickNotes }, employeeSession);
      }
      cancelEdit();
    } catch (error) {
      notifyError(error);
    } finally {
      formInFlightRef.current = false;
      categoryWriteInFlightRef.current = false;
      setIsSavingForm(false);
    }
  }, [rows, employeeSession, editingId, editingBaseline, tenantId, cancelEdit]);

  // Summary: Per-id click guard prevents duplicate lifecycle PATCHes from rapid taps.
  // Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
  const toggleActive = useCallback(async (category: CategoryView) => {
    if (!employeeSession || togglingIdRef.current !== null || categoryWriteInFlightRef.current) return;
    togglingIdRef.current = category.id;
    categoryWriteInFlightRef.current = true;
    setTogglingId(category.id);
    try {
      if (category.isActive) {
        await categoriesService.deactivate(category.id, employeeSession);
      } else {
        await categoriesService.activate(category.id, employeeSession);
      }
    } catch (error) {
      notifyError(error);
    } finally {
      togglingIdRef.current = null;
      categoryWriteInFlightRef.current = false;
      setTogglingId(null);
    }
  }, [employeeSession]);

  const setCategoryReportGroup = useCallback(async (category: CategoryView, reportGroup: CategoryReportGroup) => {
    if (!employeeSession || reportGroupInFlightRef.current !== null || categoryWriteInFlightRef.current) return;
    const nextReportGroup = category.reportGroup === reportGroup ? null : reportGroup;
    reportGroupInFlightRef.current = category.id;
    categoryWriteInFlightRef.current = true;
    setReportGroupSavingId(category.id);
    try {
      await categoriesService.update(category.id, { reportGroup: nextReportGroup }, employeeSession);
    } catch (error) {
      notifyError(error);
    } finally {
      reportGroupInFlightRef.current = null;
      categoryWriteInFlightRef.current = false;
      setReportGroupSavingId(null);
    }
  }, [employeeSession]);

  // Summary: Immediate-save inline parent picker in the manager table row (mirrors setCategoryReportGroup).
  // Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
  const setCategoryParent = useCallback(async (category: CategoryView, parentCategoryPk: number | null) => {
    if (!employeeSession || parentInFlightRef.current !== null || categoryWriteInFlightRef.current) return;
    if (parentCategoryPk === category.parentCategoryPk) return;
    parentInFlightRef.current = category.id;
    categoryWriteInFlightRef.current = true;
    setParentSavingId(category.id);
    try {
      await categoriesService.update(category.id, { parentCategoryPk }, employeeSession);
    } catch (error) {
      notifyError(error);
    } finally {
      parentInFlightRef.current = null;
      categoryWriteInFlightRef.current = false;
      setParentSavingId(null);
    }
  }, [employeeSession]);

  const toggleProduct = useCallback((productId: number) => {
    if (membershipInFlightRef.current) return;
    setCheckedProductIds((previous) => {
      const next = new Set(previous);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  const saveMembership = useCallback(async () => {
    if (membershipCategoryId === null || !employeeSession || membershipInFlightRef.current || categoryWriteInFlightRef.current) return;
    const initial = initialCheckedRef.current;
    const addProductIds = Array.from(checkedProductIds).filter((id) => !initial.has(id));
    const removeProductIds = Array.from(initial).filter((id) => !checkedProductIds.has(id));
    // Diff-only (Rule 19): nothing changed → close, no request.
    if (addProductIds.length === 0 && removeProductIds.length === 0) {
      setMembershipCategoryId(null);
      return;
    }
    membershipInFlightRef.current = true;
    categoryWriteInFlightRef.current = true;
    setIsSavingMembership(true);
    try {
      await categoriesService.setMembership(membershipCategoryId, addProductIds, removeProductIds, employeeSession);
      notifications.show({ color: 'teal', title: 'נשמר', message: 'שיוך המוצרים עודכן', autoClose: 3000 });
      setMembershipCategoryId(null);
    } catch (error) {
      notifyError(error);
    } finally {
      membershipInFlightRef.current = false;
      categoryWriteInFlightRef.current = false;
      setIsSavingMembership(false);
    }
  }, [membershipCategoryId, employeeSession, checkedProductIds]);

  const isAnyWriteInFlight = isSavingForm || isSavingMembership || togglingId !== null || reportGroupSavingId !== null || parentSavingId !== null;

  const parentCategoryOptions = useMemo(
    () => getSelectableParentOptions(categories, editingId),
    [categories, editingId],
  );

  // Summary: Per-row parent-picker options for the inline table column, excluding
  // that row's own category (self/descendant/depth-cap), same rule as the edit form.
  const parentOptionsByCategory = useMemo(() => {
    const map = new Map<number, { value: string; label: string }[]>();
    for (const category of categories) {
      map.set(category.id, getSelectableParentOptions(categories, category.id));
    }
    return map;
  }, [categories]);

  return {
    rows,
    updateRowField,
    parentCategoryOptions,
    parentOptionsByCategory,
    addRow,
    removeRow,
    editingId,
    sorted,
    orderedCategories,
    reorderCategory,
    activeProducts,
    categoryById,
    productCountByCategory,
    membershipCategoryId,
    setMembershipCategoryId,
    membershipCategory,
    checkedProductIds,
    isSavingForm,
    isSavingMembership,
    togglingId,
    reportGroupSavingId,
    parentSavingId,
    isAnyWriteInFlight,
    cancelEdit,
    startEdit,
    submitForm,
    toggleActive,
    setCategoryReportGroup,
    setCategoryParent,
    toggleProduct,
    saveMembership,
  };
}
